'use client';

/**
 * GallerySection — image carousel for rail cards.
 *
 * Cards-everywhere-native primitive. Mirrors the rendering
 * `<ImageSection>` provides for the legacy CopilotPanel but lives
 * under `rail-insights/sections/` so it isn't coupled to the legacy
 * details infrastructure — when CopilotPanel retires, the legacy
 * sections retire with it; this primitive stays.
 *
 * Single image + prev/next nav for galleries with multiple URLs.
 * Auto-hides when the URL list is empty.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot / rail-insights
 */

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

interface GallerySectionProps {
  urls: string[] | null | undefined;
  alt?: string;
  aspectRatio?: 'square' | '16:9' | '4:3';
}

const ASPECT_CLASS = {
  square: 'aspect-square',
  '16:9': 'aspect-video',
  '4:3': 'aspect-[4/3]',
} as const;

export function GallerySection({
  urls,
  alt = 'Image',
  aspectRatio = 'square',
}: GallerySectionProps) {
  const [idx, setIdx] = useState(0);

  const gallery = urls ?? [];
  if (gallery.length === 0) return null;

  const hasMultiple = gallery.length > 1;

  return (
    <div className={cn('relative bg-muted rounded overflow-hidden', ASPECT_CLASS[aspectRatio])}>
      <img
        src={gallery[idx]}
        alt={hasMultiple ? `${alt} — ${idx + 1}/${gallery.length}` : alt}
        className="w-full h-full object-contain"
        loading="lazy"
      />
      {hasMultiple && (
        <>
          <button
            type="button"
            onClick={() => setIdx((i) => (i === 0 ? gallery.length - 1 : i - 1))}
            className="absolute left-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-background/80 hover:bg-background transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => (i === gallery.length - 1 ? 0 : i + 1))}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-background/80 hover:bg-background transition-colors"
            aria-label="Next image"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[10px] rounded bg-background/80 tabular-nums">
            {idx + 1}/{gallery.length}
          </div>
        </>
      )}
    </div>
  );
}
